import React from 'react';
import { OverlayPanel } from '../ui/OverlayPanel';

interface QuestObjective {
  eventType: string;
  targetCount: number;
  weight: number;
  locationId?: string;
  npcId?: string;
}

interface QuestDefinition {
  id: string;
  order: number;
  name: string;
  description: string;
  objective: string;
  hint: string;
  targetCount: number;
  objectives?: QuestObjective[];
  rewards: Array<{
    type: string;
    amount?: number;
    itemId?: string;
    title?: string;
  }>;
  mentorDialogue: {
    start: string;
    progress: string;
    complete: string;
  };
  questGiver?: {
    id: string;
    name: string;
    title: string;
    portrait: string;
  };
  lore?: string;
}

interface QuestProgress {
  questId: string;
  status: 'locked' | 'active' | 'completed';
  currentCount: number;
  completedAt?: number;
  metadata?: Record<string, number>;
}

interface QuestsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  quests: Record<string, QuestProgress>;
  questDefinitions: QuestDefinition[];
  starterVoyageComplete: boolean;
  onSetDestination?: (x: number, y: number, name: string) => void;
  onOpenQuestJournal?: (questId: string) => void;
  trackedQuestId?: string | null;
  onTrackQuest?: (questId: string) => void;
}

const FONT = {
  family: '"Sometype Mono", monospace',
  sizeSmall: 11,
  sizeMedium: 12,
  sizeLarge: 13,
  weightNormal: 400,
  weightBold: 700,
};

const COLORS = {
  text: '#e6edf3',
  textSecondary: '#8b949e',
  textTertiary: '#94a3b8',
  bgPrimary: '#0d1117',
  bgSecondary: '#0f172a',
  bgTertiary: '#0b1220',
  border: '#30363d',
  borderLight: '#1f2937',
  accentGreen: '#22c55e',
  accentBlue: '#1f6feb',
  accentOrange: '#f59e0b',
  accentGold: '#b8860b',
  accentPurple: '#8b5cf6',
};

export const QuestsPanel: React.FC<QuestsPanelProps> = ({
  isOpen,
  onClose,
  quests,
  questDefinitions,
  starterVoyageComplete,
  onSetDestination,
  onOpenQuestJournal,
  trackedQuestId,
  onTrackQuest,
}) => {
  // Quest location coordinates for navigation (based on seedPermanentLocations.ts generation)
  // WORLD_SIZE = 8000, center = 4000, with jitter applied
  const questLocationCoords: Record<string, { x: number; y: number; name: string }> = {
    trading_post_paradise: { x: 4000, y: 4000, name: 'Grand Latitude Exchange' },
    trading_post_eastern_reach: { x: 6200, y: 1800, name: 'Eastern Reach Market' },
    trading_post_arcadia: { x: 4000, y: 5200, name: 'Arcadia Central Market' },
  };

  // Quest start locations for locked quests
  const questStartLocations: Record<string, { x: number; y: number; name: string; npc: string } | null> = {
    bon_voyage: { x: 4000, y: 4000, name: 'Safe Harbor', npc: 'Captain Aldric' },
    first_steps: { x: 4000, y: 4000, name: 'Grand Latitude Exchange', npc: 'Merchant' },
    cooks_assistant: null, // On ship, no navigation needed
    message_courier: { x: 4000, y: 4000, name: 'Grand Latitude Exchange', npc: 'Merchant Vex' },
  };
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return COLORS.accentGreen;
      case 'completed':
        return COLORS.accentBlue;
      case 'locked':
        return COLORS.textSecondary;
      default:
        return COLORS.textSecondary;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'active':
        return 'IN PROGRESS';
      case 'completed':
        return 'COMPLETED';
      case 'locked':
        return 'LOCKED';
      default:
        return 'UNKNOWN';
    }
  };

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      title="QUESTS"
      draggable
      storageKey="questsPanelPosition"
      defaultPosition={{ x: 100, y: 100 }}
      clampToViewport
      closeOnEscape
      closeOnBackdrop
      width={500}
    >
      <div
        style={{
          padding: '16px',
          maxHeight: '600px',
          overflowY: 'auto',
          fontFamily: FONT.family,
        }}
      >
        {starterVoyageComplete && (
          <div
            style={{
              padding: '12px',
              backgroundColor: COLORS.bgTertiary,
              border: `1px solid ${COLORS.accentGreen}`,
              marginBottom: '16px',
              fontSize: FONT.sizeSmall,
              color: COLORS.accentGreen,
              textAlign: 'center',
              fontWeight: FONT.weightBold,
              letterSpacing: 1,
            }}
          >
            [COMPLETE] STARTER VOYAGE COMPLETE
          </div>
        )}

        {questDefinitions.length === 0 && (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              color: COLORS.textSecondary,
              fontSize: FONT.sizeMedium,
            }}
          >
            No quests available at this time.
          </div>
        )}

        {questDefinitions.map((questDef) => {
          const progress = quests[questDef.id];
          const status = progress?.status || 'locked';
          const currentCount = progress?.currentCount || 0;

          const statusColor = getStatusColor(status);
          const statusLabel = getStatusLabel(status);
          const progressPercent = (currentCount / questDef.targetCount) * 100;

          return (
            <div
              key={questDef.id}
              style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: COLORS.bgSecondary,
                border: `1px solid ${COLORS.border}`,
              }}
            >
              {/* Quest Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: FONT.sizeLarge,
                    color: COLORS.text,
                    fontWeight: FONT.weightBold,
                  }}
                >
                  {questDef.name}
                </h3>
                <span
                  style={{
                    fontSize: FONT.sizeSmall,
                    color: statusColor,
                    fontWeight: FONT.weightBold,
                    letterSpacing: 0.5,
                  }}
                >
                  {statusLabel}
                </span>
              </div>

              {/* Quest Description */}
              <p
                style={{
                  margin: '0 0 8px 0',
                  fontSize: FONT.sizeSmall,
                  color: COLORS.textSecondary,
                  lineHeight: 1.5,
                }}
              >
                {questDef.description}
              </p>

              {/* Quest Giver Info */}
              {questDef.questGiver && (
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '8px',
                    padding: '8px',
                    backgroundColor: COLORS.bgTertiary,
                    border: `1px solid ${COLORS.borderLight}`,
                    borderRadius: '2px',
                  }}
                >
                  <img
                    src={questDef.questGiver.portrait}
                    alt={questDef.questGiver.name}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '2px',
                      border: `1px solid ${COLORS.border}`,
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="40" height="40"%3E%3Crect fill="%230b1220" width="40" height="40"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%238b949e" font-size="10"%3E[NPC]%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: FONT.sizeSmall,
                        color: COLORS.accentBlue,
                        fontWeight: FONT.weightBold,
                      }}
                    >
                      {questDef.questGiver.name}
                    </div>
                    <div
                      style={{
                        fontSize: FONT.sizeSmall,
                        color: COLORS.textTertiary,
                      }}
                    >
                      {questDef.questGiver.title}
                    </div>
                  </div>
                </div>
              )}

              {/* Quest Objective */}
              <div
                style={{
                  padding: '8px',
                  backgroundColor: COLORS.bgTertiary,
                  border: `1px solid ${COLORS.borderLight}`,
                  marginBottom: '8px',
                }}
              >
                <div
                  style={{
                    fontSize: FONT.sizeSmall,
                    color: COLORS.textTertiary,
                    marginBottom: '4px',
                  }}
                >
                  OBJECTIVE:
                </div>
                <div
                  style={{
                    fontSize: FONT.sizeSmall,
                    color: COLORS.text,
                  }}
                >
                  {questDef.objective}
                </div>
              </div>

              {/* Navigation for Locked Quests */}
              {status === 'locked' && questStartLocations[questDef.id] && (
                <div
                  style={{
                    marginBottom: '8px',
                    paddingTop: '8px',
                    borderTop: `1px solid ${COLORS.borderLight}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: FONT.sizeSmall,
                      color: COLORS.textTertiary,
                      marginBottom: '6px',
                    }}
                  >
                    TO START THIS QUEST:
                  </div>
                  <button
                    onClick={() => {
                      const startLoc = questStartLocations[questDef.id];
                      if (onSetDestination && startLoc) {
                        onSetDestination(startLoc.x, startLoc.y, startLoc.name);
                        onClose();
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      backgroundColor: COLORS.accentOrange,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '2px',
                      fontFamily: FONT.family,
                      fontSize: FONT.sizeSmall,
                      fontWeight: FONT.weightBold,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = '#d97706';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = COLORS.accentOrange;
                    }}
                  >
                    → Head to {questStartLocations[questDef.id]?.name} ({questStartLocations[questDef.id]?.npc})
                  </button>
                </div>
              )}

              {/* Begin Quest Button for Ship-Based Quests */}
              {status === 'locked' && !questStartLocations[questDef.id] && (
                <div
                  style={{
                    marginBottom: '8px',
                    paddingTop: '8px',
                    borderTop: `1px solid ${COLORS.borderLight}`,
                  }}
                >
                  <button
                    onClick={() => {
                      // Emit quest start event to server
                      // This will activate the quest and trigger the first NPC dialogue
                      onClose();
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      backgroundColor: COLORS.accentGreen,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '2px',
                      fontFamily: FONT.family,
                      fontSize: FONT.sizeSmall,
                      fontWeight: FONT.weightBold,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = '#16a34a';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = COLORS.accentGreen;
                    }}
                  >
                    ▶ BEGIN QUEST
                  </button>
                </div>
              )}

              {/* Progress Bar */}
              {status === 'active' && (
                <div style={{ marginBottom: '8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '4px',
                      fontSize: FONT.sizeSmall,
                      color: COLORS.textSecondary,
                    }}
                  >
                    <span>Progress</span>
                    <span>
                      {currentCount} / {questDef.targetCount}
                    </span>
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: COLORS.bgTertiary,
                      border: `1px solid ${COLORS.borderLight}`,
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(100, progressPercent)}%`,
                        height: '100%',
                        backgroundColor: COLORS.accentGreen,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Hint */}
              {status === 'active' && questDef.hint && (
                <div
                  style={{
                    fontSize: FONT.sizeSmall,
                    color: COLORS.textTertiary,
                    fontStyle: 'italic',
                    marginTop: '8px',
                    marginBottom: '8px',
                  }}
                >
                  💡 {questDef.hint}
                </div>
              )}

              {/* Track Quest & Quest Journal Buttons */}
              {status === 'active' && (
                <div
                  style={{
                    display: 'flex',
                    gap: '6px',
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: `1px solid ${COLORS.borderLight}`,
                  }}
                >
                  <button
                    onClick={() => {
                      if (onTrackQuest) {
                        onTrackQuest(questDef.id);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      backgroundColor: trackedQuestId === questDef.id ? COLORS.accentGreen : COLORS.accentBlue,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '2px',
                      fontFamily: FONT.family,
                      fontSize: FONT.sizeSmall,
                      fontWeight: FONT.weightBold,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = trackedQuestId === questDef.id ? '#16a34a' : '#3b82f6';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLButtonElement).style.backgroundColor = trackedQuestId === questDef.id ? COLORS.accentGreen : COLORS.accentBlue;
                    }}
                  >
                    {trackedQuestId === questDef.id ? 'TRACKED' : 'TRACK'}
                  </button>
                  {questDef.lore && (
                    <button
                      onClick={() => {
                        if (onOpenQuestJournal) {
                          onOpenQuestJournal(questDef.id);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '6px 8px',
                        backgroundColor: COLORS.accentPurple,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '2px',
                        fontFamily: FONT.family,
                        fontSize: FONT.sizeSmall,
                        fontWeight: FONT.weightBold,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        (e.target as HTMLButtonElement).style.backgroundColor = '#a78bfa';
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLButtonElement).style.backgroundColor = COLORS.accentPurple;
                      }}
                    >
                      📖 JOURNAL
                    </button>
                  )}
                </div>
              )}

              {/* Navigation Buttons for Quest Objectives */}
              {status === 'active' && questDef.id === 'first_steps' && (
                <div
                  style={{
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: `1px solid ${COLORS.borderLight}`,
                    display: 'flex',
                    gap: '6px',
                    flexWrap: 'wrap',
                  }}
                >
                  {questDef.objectives?.map((obj, idx) => {
                    if (obj.eventType === 'location_visited' && obj.locationId) {
                      const coords = questLocationCoords[obj.locationId];
                      if (!coords) return null;
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            if (onSetDestination && coords) {
                              onSetDestination(coords.x, coords.y, coords.name);
                              onClose();
                            }
                          }}
                          style={{
                            flex: '1 1 auto',
                            padding: '6px 8px',
                            backgroundColor: COLORS.accentBlue,
                            color: '#fff',
                            border: 'none',
                            borderRadius: '2px',
                            fontFamily: FONT.family,
                            fontSize: FONT.sizeSmall,
                            fontWeight: FONT.weightBold,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            (e.target as HTMLButtonElement).style.backgroundColor = '#3b82f6';
                          }}
                          onMouseLeave={(e) => {
                            (e.target as HTMLButtonElement).style.backgroundColor = COLORS.accentBlue;
                          }}
                        >
                          → {coords.name}
                        </button>
                      );
                    }
                    return null;
                  })}
                </div>
              )}

              {/* Rewards */}
              <div
                style={{
                  marginTop: '8px',
                  paddingTop: '8px',
                  borderTop: `1px solid ${COLORS.borderLight}`,
                }}
              >
                <div
                  style={{
                    fontSize: FONT.sizeSmall,
                    color: COLORS.textTertiary,
                    marginBottom: '4px',
                  }}
                >
                  REWARDS:
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  {questDef.rewards.map((reward, idx) => (
                    <span
                      key={idx}
                      style={{
                        fontSize: FONT.sizeSmall,
                        color: COLORS.accentGold,
                        fontWeight: FONT.weightBold,
                      }}
                    >
                      {reward.type === 'pieces' && `₱${reward.amount?.toLocaleString()}`}
                      {reward.type === 'xp' && `${reward.amount} XP`}
                      {reward.type === 'item' && `${reward.amount}x ${reward.itemId}`}
                      {reward.type === 'title' && `Title: ${reward.title}`}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </OverlayPanel>
  );
};
